import React, { useState } from 'react';
import { Card } from '../../../components/Card/Card';
import { t } from '../../../locales/translate';
import { Product, getProductSaleStats, SalesStat } from '../../../model/product';
import { SortableList, ColumnContent } from '../../../components/SortableList/SortableList';
import { useAsync, useTask } from '../../../model/data';
import { useErrorHandler, formatDate, formatNumber } from '../../../util/util';
import { sortBy } from 'lodash';

import { Icon } from '../../../components/Icon/Icon';
import classnames from 'classnames';
import { runBundleConfigExport } from '../../../model/exports';

interface Props {
  product: Product;
}

export function Sales({ product }: Props): JSX.Element {
  const [loading, error] = useAsync(() => getProductSaleStats(product.id));
  useErrorHandler(error);
  const [expanded, setExpanded] = useState(false);
  const lastYear = new Date().getFullYear() - 1;
  const exportTask = useTask({ handleError: true });

  function renderField(value: number, row: SalesStat): JSX.Element {
    return row.name === 'aggr' ? (
      <span className="sales__aggregate">{formatNumber(value, 0)}</span>
    ) : (
      <span>{formatNumber(value, 0)}</span>
    );
  }

  function getLast6Months(): Array<ColumnContent<SalesStat>> {
    return [0, 1, 2, 3, 4, 5].map((item) => {
      return {
        dataKey: `month${item}` as 'month0' | 'month1' | 'month2' | 'month3' | 'month4' | 'month5',
        label: formatDate(product.salesStats?.[0]?.[`month${item}`]?.date, undefined, {
          month: '2-digit',
          year: 'numeric',
        }),
        cellRenderer: (row: SalesStat) => renderField(row[`month${item}`]?.online, row),
      };
    });
  }

  const tableColumns: Array<ColumnContent<SalesStat>> = [
    {
      dataKey: 'name',
      label: t.model.variant._name,
      cellRenderer: (row) => (row.name === 'aggr' ? '' : row.name || t.general.notAvailable),
    },
    {
      dataKey: 'lifetime',
      label: t.general.lifeTime,
      separator: true,
      cellRenderer: (row) => renderField(row.lifetime.online, row),
    },
    {
      dataKey: 'lastYear',
      label: lastYear,
      separator: true,
      cellRenderer: (row) => renderField(row.lastYear.online, row),
    },
    ...(expanded ? getLast6Months() : []),
    {
      dataKey: 'lastSixMonths',
      label: t.general.lastSixMonths,
      separator: true,
      cellRenderer: (row) => renderField(row.lastSixMonths?.online, row),
    },
    {
      dataKey: 'lastThirtyDays',
      label: t.general.lastThirtyDays,
      separator: true,
      cellRenderer: (row) => renderField(row.lastThirtyDays.online, row),
    },
    {
      dataKey: 'today',
      label: t.general.today,
      cellRenderer: (row) => renderField(row.today?.online, row),
    },
    {
      dataKey: 'hour',
      label: t.general.lastHour,
      cellRenderer: (row) => renderField(row.hour?.online, row),
    },
  ];

  return (
    <Card
      title={t.model.sale._plural}
      actions={
        <>
          <Icon name="expand" onClick={() => setExpanded((expanded) => !expanded)} loading={loading} />
          {product.productType === 'Bundle' && (
            <Icon
              name="download--sm"
              tooltip={t.model.product.bundleConfigExport}
              loading={exportTask.loading}
              onClick={() =>
                exportTask.run(
                  runBundleConfigExport({
                    productId: product.id,
                  })
                )
              }
            />
          )}
        </>
      }
      className={classnames('product-sales', {
        'product-sales--expanded': expanded,
      })}
      elevation={1}
      fullbleed
    >
      {!loading && (
        <SortableList
          items={sortBy(product.salesStats, ['order'])}
          columns={tableColumns}
          scrollPadding="0"
          defaultSort={{ sortBy: 'order', sortAscending: true }}
        />
      )}
    </Card>
  );
}
